import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ResiduosSelector from "@/components/puntolimpio/ResiduosSelector";
import QrScannerModal from "@/components/puntolimpio/QrScannerModal";
import { CheckCircle2, XCircle, Camera, Loader2, QrCode, Search } from "lucide-react";
import { toast } from "sonner";
import { normalizeDniNie } from "@/lib/dni";
import { getErrorMessage } from "@/lib/errors";
import {
  adminListCheckins, adminConfirmarCheckin, adminRechazarCheckin,
  adminSubirAdjuntoPuntoLimpio, getCatalogoResiduos,
} from "@/api";

export default function CheckinsPendientes({ onConfirmed }) {
  const [checkins, setCheckins] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [current, setCurrent] = useState(null);
  const [fechaHora, setFechaHora] = useState("");
  const [residuos, setResiduos] = useState([]);
  const [incidencias, setIncidencias] = useState(false);
  const [tipoIncidencia, setTipoIncidencia] = useState("");
  const [foto, setFoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [search, setSearch] = useState("");

  const load = () => adminListCheckins("pendiente").then(setCheckins).catch(() => {});

  useEffect(() => {
    load();
    getCatalogoResiduos().then(setCatalogo).catch(() => {});
  }, []);

  const openConfirm = (c) => {
    setCurrent(c);
    setFechaHora(new Date().toISOString().slice(0, 16));
    setResiduos(c.residuos || []);
    setIncidencias(false);
    setTipoIncidencia("");
    setFoto(null);
  };

  const handleScanResult = useCallback(async (decodedText) => {
    setShowScanner(false);
    let data;
    try {
      data = JSON.parse(decodedText);
    } catch {
      toast.error("Ese código QR no es válido para este formulario");
      return;
    }
    const dni = normalizeDniNie(data.dni_nie || data.cif || "");
    // Siempre recarga la lista antes de buscar: el vecino puede haber rellenado el
    // formulario después de que el personal abriera este panel.
    const fresh = await adminListCheckins("pendiente").catch(() => []);
    setCheckins(fresh);
    onConfirmed?.();
    const match = fresh.find((c) => normalizeDniNie(c.dni_nie) === dni);
    if (match) {
      openConfirm(match);
      toast.success(`Check-in encontrado: ${match.codigo}`);
    } else {
      toast.error("No hay ningún check-in pendiente con ese DNI/CIF. Puede que ya esté confirmado o que el vecino aún no haya rellenado el formulario.");
    }
  }, [onConfirmed]);

  const filteredCheckins = checkins.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const qDni = normalizeDniNie(q);
    return (
      c.codigo?.toLowerCase().includes(q) ||
      c.nombre_apellidos?.toLowerCase().includes(q) ||
      c.dni_nie?.toLowerCase().includes(q) ||
      (qDni && normalizeDniNie(c.dni_nie).includes(qDni))
    );
  });

  const handleConfirmar = async () => {
    setSaving(true);
    try {
      const res = await adminConfirmarCheckin(current.id, {
        fecha_hora_entrega: new Date(fechaHora).toISOString(),
        residuos,
        incidencias,
        tipoincidencia: incidencias ? tipoIncidencia : null,
      });
      if (foto && res.arcgis_object_id) {
        await adminSubirAdjuntoPuntoLimpio(res.arcgis_object_id, "foto_incidencia_entrada", foto);
      }
      toast.success(`Entrada confirmada (ArcGIS #${res.arcgis_object_id})`);
      setCurrent(null);
      load();
      onConfirmed?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo confirmar la entrada"));
    } finally {
      setSaving(false);
    }
  };

  const handleRechazar = async (id) => {
    try {
      await adminRechazarCheckin(id);
      toast.success("Check-in rechazado");
      load();
    } catch {
      toast.error("No se pudo rechazar el check-in");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="checkins-pendientes-panel">
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-100">
        <Button
          onClick={() => setShowScanner(true)}
          className="bg-municipal hover:bg-municipal-dark text-white"
          data-testid="checkins-escanear-qr-btn"
        >
          <QrCode className="w-4 h-4 mr-2" /> Escanear QR del vecino
        </Button>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código, nombre o DNI/CIF..."
            className="pl-9"
            data-testid="checkins-buscar-input"
          />
        </div>
        <span className="text-xs text-gray-400 shrink-0">{filteredCheckins.length} de {checkins.length} pendientes</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead>
            <TableHead>Ciudadano</TableHead>
            <TableHead className="hidden md:table-cell">Dirección</TableHead>
            <TableHead>Residuos</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredCheckins.length === 0 && (
            <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-10">
              {checkins.length === 0 ? "No hay check-ins pendientes." : "Ningún check-in coincide con la búsqueda."}
            </TableCell></TableRow>
          )}
          {filteredCheckins.map((c) => (
            <TableRow key={c.id} data-testid={`checkin-row-${c.codigo}`}>
              <TableCell className="font-medium text-municipal">{c.codigo}</TableCell>
              <TableCell>
                {c.tipo_solicitante === "empresa" ? (
                  <span className="flex items-center gap-1.5 flex-wrap">
                    {c.nombre_apellidos}
                    <Badge className="bg-municipal text-white text-[10px]">Empresa</Badge>
                    {c.origen === "telefono" && <Badge className="bg-dorado text-white text-[10px]" data-testid={`checkin-origen-telefono-badge-${c.codigo}`}>Teléfono</Badge>}
                    <br />
                    <span className="text-xs text-gray-400 w-full">CIF: {c.dni_nie}</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 flex-wrap">
                    {c.nombre_apellidos}
                    {c.origen === "telefono" && <Badge className="bg-dorado text-white text-[10px]" data-testid={`checkin-origen-telefono-badge-${c.codigo}`}>Teléfono</Badge>}
                    <br />
                    <span className="text-xs text-gray-400 w-full">{c.dni_nie}</span>
                  </span>
                )}
              </TableCell>
              <TableCell className="hidden md:table-cell">{c.direccion}</TableCell>
              <TableCell><Badge variant="outline">{c.residuos?.length || 0} tipo(s)</Badge></TableCell>
              <TableCell className="text-right space-x-1">
                <Button size="sm" className="bg-valoriza-green hover:bg-valoriza-green/90" onClick={() => openConfirm(c)} data-testid={`checkin-confirm-btn-${c.codigo}`}>
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" className="border-red-300 text-red-500" onClick={() => handleRechazar(c.id)} data-testid={`checkin-reject-btn-${c.codigo}`}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!current} onOpenChange={() => !saving && setCurrent(null)}>
        <DialogContent className="max-w-lg" data-testid="confirmar-entrada-dialog">
          {current && (
            <>
              <DialogHeader><DialogTitle className="text-municipal font-heading">Confirmar entrada — {current.codigo}</DialogTitle></DialogHeader>
              <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                <div>
                  <Label className="mb-1.5 block">Fecha y hora de entrega efectiva</Label>
                  <Input type="datetime-local" value={fechaHora} onChange={(e) => setFechaHora(e.target.value)} data-testid="confirmar-fecha-input" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Residuos verificados (kg/litros/unidades reales)</Label>
                  {catalogo.length > 0 && <ResiduosSelector catalogo={catalogo} value={residuos} onChange={setResiduos} />}
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={incidencias} onCheckedChange={setIncidencias} id="inc-entrada" data-testid="confirmar-incidencias-checkbox" />
                  <Label htmlFor="inc-entrada" className="font-normal cursor-pointer">Ha habido alguna incidencia</Label>
                </div>
                {incidencias && (
                  <Input placeholder="Describe la incidencia" value={tipoIncidencia} onChange={(e) => setTipoIncidencia(e.target.value)} data-testid="confirmar-tipoincidencia-input" />
                )}
                <div>
                  <Label className="mb-1.5 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Foto (opcional)</Label>
                  <Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFoto(e.target.files?.[0] || null)} data-testid="confirmar-foto-input" />
                </div>
              </div>
              <Button
                className="w-full bg-municipal hover:bg-municipal-dark text-white mt-2"
                onClick={handleConfirmar}
                disabled={saving || residuos.length === 0}
                data-testid="confirmar-entrada-submit-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Confirmar entrada en ArcGIS
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <QrScannerModal open={showScanner} onClose={() => setShowScanner(false)} onResult={handleScanResult} title="Escanear QR del vecino" />
    </div>
  );
}
